import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Trips from './pages/Trips';
import PrivateRoute from './components/PrivateRoute';
import Drivers from './pages/Drivers';
import DriverExpenses from './pages/DriverExpense';
import DriverAttendancePage from './pages/DriverAttendance';
import Branches from './pages/Branches';
import BranchUsers from './pages/BranchUsers';
import Vehicles from './pages/Vehicles';
import VehicleExpenses from './pages/VehicleExpense';
import Clients from './pages/Clients';
import Customers from './pages/Customers';
import Reports from './pages/Reports';
import DriverExpenseReport from './pages/DriverExpenseReport';
import VehicleExpenseReport from './pages/VehicleExpenseReport';
import AttendanceReport from './pages/AttendanceReport';
import Profile from './pages/Profile';
import AdminProfile from './pages/AdminProfile';
import { useVersionChecker } from './context/app_version';
import { ToastContainer, toast } from 'react-toastify';
import { shouldAutoUppercaseField } from './config/textCase';
import 'react-toastify/dist/ReactToastify.css';

const App = () => {
  const location = useLocation();
  const hideNavbar = ['/login', '/forgot-password'].includes(location.pathname);

  const { isNew, currentVersion } = useVersionChecker();

  useEffect(() => {
    if (isNew) {
      toast.info(
        <div>
          <p>🚀 A new version of the app is available.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 bg-blue-600 text-white px-2 py-1 rounded"
          >
            Refresh Now
          </button>
        </div>,
        { autoClose: false }
      );
    }
  }, [isNew]);

  useEffect(() => {
    const transformToUppercase = (event) => {
      const element = event?.target;
      if (!shouldAutoUppercaseField(element)) return;

      const originalValue = element.value;
      const upperValue = originalValue.toUpperCase();
      if (originalValue === upperValue) return;
      element.value = upperValue;
      element.dispatchEvent(new Event('input', { bubbles: true }));
    };

    document.addEventListener('change', transformToUppercase, true);
    return () => document.removeEventListener('change', transformToUppercase, true);
  }, []);

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100 text-gray-800">
      {!hideNavbar && <Sidebar />}
      <div className="flex-1 w-full">
        <main className={`p-4 overflow-visible min-h-screen pt-14 md:pt-4 ${!hideNavbar ? 'md:ml-60' : ''}`}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route element={<PrivateRoute />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/trips" element={<Trips />} />
              <Route path="/branches" element={<Branches />} />
              <Route path="/branch-admins" element={<BranchUsers />} />
              <Route
                path="/branch-users"
                element={<BranchUsers restrictToBranchUsersOnly={true} />}
              />
              <Route path="/drivers" element={<Drivers />} />
              <Route path="/driver-expenses" element={<DriverExpenses />} />
              <Route path="/vehicles" element={<Vehicles />} />
              <Route path="/vehicle-expenses" element={<VehicleExpenses />} />
              <Route path="/attendance" element={<DriverAttendancePage />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/reports">
                <Route index element={<Navigate to="trips" replace />} />
                <Route path="trips" element={<Reports />} />
                <Route path="driver-expense" element={<DriverExpenseReport />} />
                <Route path="vehicle-expense" element={<VehicleExpenseReport />} />
                <Route path="attendance" element={<AttendanceReport />} />
              </Route>
              <Route path="/profile" element={<Profile />} />
              <Route path="/admin/profile" element={<AdminProfile />} />
            </Route>
          </Routes>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </main>
      </div>
    </div>
  );
};

export default App;
